import React, { useState, useEffect, useLayoutEffect } from 'react';
import { questions } from '../../config/questions';
import QuestionEditComponent from './QuestionEditComponent';
import { formatAnswer } from '../../lib/formatAnswer';

const SummaryScreen = ({
  answers,
  visibleQuestions,
  onEditQuestion,
  onSubmitIntake,
  onBackToQuestions,
  submitting = false,
  submitError = '',
  submitted = false,
}) => {
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [, setEditingAnswer] = useState(null);
  const scrollPositionRef = React.useRef(0);
  const isUserScrollingRef = React.useRef(false);
  const scrollTimeoutRef = React.useRef(null);

  // Auto-scroll to top when summary screen is displayed
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Track user-initiated scrolling vs automatic scrolling
  useEffect(() => {
    if (!editingQuestionId) return;

    // Mark as user scrolling when user interacts with scroll controls
    const handleUserScrollStart = () => {
      isUserScrollingRef.current = true;

      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Reset flag after a short delay (user finished scrolling)
      scrollTimeoutRef.current = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 150);
    };

    // Track scroll and update saved position if user-initiated
    const handleScroll = () => {
      if (isUserScrollingRef.current) {
        // User is manually scrolling, update the saved position
        scrollPositionRef.current = window.scrollY;
      } else {
        // Automatic scroll (from focus/DOM changes), prevent it
        window.scrollTo({ top: scrollPositionRef.current, behavior: 'instant' });
      }
    };

    // Listen to user scroll interactions
    window.addEventListener('wheel', handleUserScrollStart, { passive: true });
    window.addEventListener('touchstart', handleUserScrollStart, { passive: true });
    window.addEventListener('touchmove', handleUserScrollStart, { passive: true });

    // Listen to scroll events
    window.addEventListener('scroll', handleScroll, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleUserScrollStart);
      window.removeEventListener('touchstart', handleUserScrollStart);
      window.removeEventListener('touchmove', handleUserScrollStart);
      window.removeEventListener('scroll', handleScroll);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [editingQuestionId]);

  // Preserve scroll position during editing using layoutEffect to prevent visible jumps
  useLayoutEffect(() => {
    if (editingQuestionId && scrollPositionRef.current > 0) {
      // Restore scroll position before browser paints
      window.scrollTo({ top: scrollPositionRef.current, behavior: 'instant' });
    }
  }); // Run on every render to catch any scroll changes during editing

  const NON_EDITABLE_IDS = new Set([
    'personal-info',
    'contact-info',
    'sex',
    'state',
    'tried',
  ]);

  const handleEditClick = (questionId, currentAnswer) => {
    if (NON_EDITABLE_IDS.has(questionId)) return;
    // Save current scroll position before editing
    scrollPositionRef.current = window.scrollY;
    setEditingQuestionId(questionId);
    setEditingAnswer(currentAnswer);
  };

  const handleSaveEdit = (newAnswer) => {
    if (!editingQuestionId) return;

    // If a child mistakenly calls onNext() (no value), keep previous answer
    const currentIdx = questions.findIndex(q => q.id === editingQuestionId);
    const prevAnswer = currentIdx !== -1 ? answers[currentIdx] : undefined;
    const safeAnswer = (newAnswer === undefined) ? prevAnswer : newAnswer;

    // Save the scroll position before updating state
    const savedScroll = scrollPositionRef.current;

    onEditQuestion(editingQuestionId, safeAnswer);
    setEditingQuestionId(null);
    setEditingAnswer(null);

    // Restore scroll position after state update and re-render
    requestAnimationFrame(() => {
      window.scrollTo({ top: savedScroll, behavior: 'instant' });
    });
  };

  const handleCancelEdit = () => {
    // Save the scroll position before updating state
    const savedScroll = scrollPositionRef.current;

    setEditingQuestionId(null);
    setEditingAnswer(null);

    // Restore scroll position after state update and re-render
    requestAnimationFrame(() => {
      window.scrollTo({ top: savedScroll, behavior: 'instant' });
    });
  };

  const getSelectedState = () => {
    const stateQuestionIndex = questions.findIndex(q => q.type === 'state');
    return stateQuestionIndex !== -1 ? answers[stateQuestionIndex] : null;
  };

  const getTreatmentAnswer = () => {
    const treatmentQuestionIndex = questions.findIndex(q => q.id === 'treatment');
    return treatmentQuestionIndex !== -1 ? answers[treatmentQuestionIndex] : null;
  };

  return (
    <div className="summary-screen">
      <div className="summary-header">
        <h2>Review Your Information</h2>
        <p>Please review your responses below. If you're ready, click Submit. If you need to make changes, click Edit below each answer.</p>
      </div>

      <div className="summary-content">
        {visibleQuestions.map((question) => {
          const questionIndex = questions.findIndex(q => q.id === question.id);
          const answer = answers[questionIndex];

          // Only skip truly missing answers (null/undefined). Allow 0, false, '' etc.
          if (answer == null) return null;

          const isEditing = editingQuestionId === question.id;

          return (
            <div key={question.id} className="summary-item">
              <div className="summary-question">
                <h3>{question.text}</h3>
                {!isEditing && !submitted && !NON_EDITABLE_IDS.has(question.id) && (
                  <button
                    className="edit-button"
                    onClick={() => handleEditClick(question.id, answer)}
                  >
                    Edit
                  </button>
                )}
              </div>

              <div className="summary-answer">
                {isEditing ? (
                  <div className="editing-answer">
                    <QuestionEditComponent
                      questionType={question.type}
                      currentAnswer={answer}
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                      selectedState={getSelectedState()}
                      treatmentAnswer={getTreatmentAnswer()}
                      answers={answers}
                      questions={questions}
                    />
                  </div>
                ) : (
                  <div className="answer-display">
                    {formatAnswer(answer, question.type)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="summary-actions">
        {submitError && <p className="submit-error">Error: {submitError}</p>}
        {submitted && <p className="submit-success">Thanks! Your intake was submitted.</p>}

        <button
          className="submit-button"
          onClick={() => { if (!submitted) onSubmitIntake(); }}
          disabled={submitting || submitted}
          aria-disabled={submitting || submitted}
        >
          {submitted ? "Submitted" : (submitting ? "Submitting…" : "Submit")}
        </button>
      </div>
    </div>
  );
};

export default SummaryScreen;
